import React, { useEffect, useRef } from 'react';
import { animated, config, useSpring, useSprings } from '@react-spring/web';
import { useMediaMatch, useScrollToElement } from '../../../utils';

import { DebugModal } from '../../../context/Debug';
import { headerHeight } from '../../../utils/menuHeight';
import { mergeClassNames } from '../../../utils/mergeClassnames';
import styles from './index.module.scss';
import { useRequestAnimFrameOnScroll } from '../../../context/onRequestAnimFrameOnScroll';

export default function AccordionScrollProgress({
  elements,
  listElementClassName = null,
  listElementStyle = null,
  heading = null,
  contentElems, // accordion moves over content fixed in the background, used for scrollTo on picking an accordion elem
  progressSprings,
  debug = false,
  className = null,
  style = null,
}) {
  elements = elements ? elements : [];
  let { registerCallback } = useRequestAnimFrameOnScroll();
  let { scrollTo } = useScrollToElement({ yOffset: headerHeight }, config.slow); // offset same as header height
  let totalElemHeight = useRef(0);
  let expandingElemHeights = useRef([elements.length]);
  let titleElemHeight = useRef(0);
  let staticElemHeights = useRef([elements.length]);
  let staticElemHeight = useRef(0);
  let staticElemsTotalHeight = useRef(0);
  let tallestElemHeight = useRef(0);
  let listElemsTotalHeight = useRef(0);
  // `<Fixed breakOverlay constrainOverlay>` is in portrait when max-width: 991px || orientation: portrait
  let isPortraitMode = useMediaMatch('screen and (max-width: 991px), screen and (orientation: portrait)'); // (min-width: 992px)
  // used to trigger a recalc of list elem heights when viewport size changes accross this limit:
  let isSmallScreen = useMediaMatch('screen and (max-width: 991px)');

  useEffect(() => {
    tallestElemHeight.current = expandingElemHeights.current.reduce((prev, curHeight) => Math.max(prev, curHeight));
    staticElemsTotalHeight.current = staticElemHeights.current.reduce((totalHeight, height) => totalHeight + height);
    let [staticHeightFirstElem = 0] = staticElemHeights.current;
    staticElemHeight.current = staticHeightFirstElem;
    listElemsTotalHeight.current =
      staticElemsTotalHeight.current + staticElemHeight.current + tallestElemHeight.current;
  }, [isPortraitMode, isSmallScreen]);

  let findElemHeight = ref => ref?.getBoundingClientRect()?.height || 0;
  let [listElemAnimProps, setListElemAnimProps] = useSprings(
    elements.length,
    () => ({ opacity: 0, transform: `translateY(${0}px)`, infoboxTopYOffset: 0 }),
    [elements]
  );

  let [totalHeightSpring, setTotalHeightSpring] = useSpring(() => ({ transform: `translateY(0px)` }), [elements]);

  useEffect(() => {
    let unregister = registerCallback(() => {
      let springHeightReducer = (total, { fadeInOutProgress }, index) => {
        return total + expandingElemHeights.current[index] * fadeInOutProgress.get();
      };
      let currentTotalExpandedHeight = progressSprings.reduce(springHeightReducer, 0);

      setTotalHeightSpring.start(() => {
        // this number defines the translate distance for the infocard - when in portrait mode it is tied to `top` and should not be translated up
        const infocardTranslateMagic =
          tallestElemHeight.current + staticElemHeight.current * 1.2 - currentTotalExpandedHeight;
        const infocardTranslate = !isPortraitMode ? infocardTranslateMagic : 0;
        return { transform: `translateY(${infocardTranslate}px)` };
      });

      // per list elem each frame
      setListElemAnimProps.start(liElemIdx => {
        let { fadeInOutProgress } = progressSprings[liElemIdx];
        let infoboxTopYOffset = progressSprings.reduce((total, progress, progIdx) => {
          if (progIdx < 1 || progIdx > liElemIdx) return total;
          let progressElemBefore = progressSprings[progIdx - 1].fadeInOutProgress.get();
          let heightElemBefore = expandingElemHeights.current[progIdx - 1];
          let activeHeightElemBefore = (1 - progressElemBefore) * heightElemBefore;
          return total + activeHeightElemBefore;
        }, 0);

        return { opacity: fadeInOutProgress, infoboxTopYOffset, immediate: true };
      });
    });

    return () => unregister();
  });

  // ? how to add these per image on print / reduced motion?
  return (
    <>
      <animated.ul
        className={mergeClassNames([styles.accordionScrollProgress, className])}
        style={{ ...totalHeightSpring, ...style }}
        ref={ref => (totalElemHeight.current = findElemHeight(ref))}
      >
        <h5 className={styles.header} ref={ref => (titleElemHeight.current = findElemHeight(ref))}>
          {heading}
        </h5>
        <div style={{ height: `${listElemsTotalHeight.current}px` }}>
          {listElemAnimProps.map(({ infoboxTopYOffset: yOffset, ...listElemAnimProp }, index) => (
            <animated.li
              className={mergeClassNames(styles.listElement, listElementClassName)}
              style={{ transform: yOffset?.to(offset => `translateY(${-offset}px)`), ...listElementStyle }}
              key={index}
            >
              <button
                onClick={() => scrollTo(Array.from(contentElems.values())[index])}
                ref={ref => (staticElemHeights.current[index] = findElemHeight(ref))}
              >
                {elements[index]?.listElement}
              </button>
              <animated.div style={listElemAnimProp}>
                <div ref={ref => (expandingElemHeights.current[index] = findElemHeight(ref))}>
                  {elements[index]?.expandingElement}
                </div>
              </animated.div>
            </animated.li>
          ))}
        </div>
      </animated.ul>
      <DebugProgress progresses={listElemAnimProps} heading={`<${AccordionScrollProgress.name}>`} debug={debug} />
    </>
  );
}

export function DebugProgress({ progresses, heading = '', className = '', style = {}, debug = false }) {
  if (!debug) return null;
  return (
    <DebugModal className={mergeClassNames(styles.debugger, className)} style={style}>
      <h3>{heading}</h3>
      {progresses.map(progress =>
        Object.keys(progress).map(key => <DebugProgressBar key={key} progress={progress[key]} label={key} />)
      )}
    </DebugModal>
  );
}

function DebugProgressBar({ label = undefined, progress }) {
  return (
    <div className={styles.debuggerProgressBar}>
      <animated.div className={styles.bar} style={{ scaleX: progress }} />
      {label && <span>{`${label}: `}</span>}
      <animated.span>{progress}</animated.span>
    </div>
  );
}
